import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import * as Sentry from '@sentry/react';

import { apiFactory } from '../../../../api/ApiFactory';

/**
 * Resolve a api_key da plataforma pelo slug da URL (`/:platform/p/...`).
 *
 * No host único do checkout não existe api_key "padrão": ela vem do slug. O
 * checkout (Single/Recurring) já fazia isso, mas a chave fica só em memória —
 * e o redirect para upsell, downsell e confirmação é um `location.replace`,
 * que recarrega a página e a perde. Essas telas não a resolviam de novo: toda
 * chamada saía sem chave (401), a confirmação mostrava só o cartão padrão,
 * sem blocos, sem pixels e sem botão, e o upsell ficava sem oferta.
 *
 * Devolve `true` quando dá para buscar dados. Ao trocar de chave, invalida as
 * queries: as que já tinham disparado sem chave (personalização da plataforma,
 * por exemplo) são refeitas com ela.
 */
export const usePlatformApiKey = (platform?: string): boolean => {
	const queryClient = useQueryClient();
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let cancelled = false;

		apiFactory
			.isDefaultApiKeyDefined()
			.then(async (isDefined) => {
				if (isDefined || !platform) return;

				const { data } = await axios.get<{ key: string }>(
					`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platforms/slug/${platform}`
				);
				if (cancelled) return;

				apiFactory.enableCustom(data.key);
				queryClient.invalidateQueries();
			})
			.catch((error) => {
				Sentry.captureException(error, {
					tags: { context: 'checkout:platform-api-key' },
					extra: { platform }
				});
			})
			.finally(() => {
				if (!cancelled) setReady(true);
			});

		return () => {
			cancelled = true;
			apiFactory.enableDefault();
		};
	}, [platform, queryClient]);

	return ready;
};
